import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Tag, Check, X, Sparkles, Crown, ChevronRight, ArrowRight, Shield, Zap, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Programs = () => {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  useEffect(() => {
    fetchPrograms();
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-config`);
      setSiteConfig(response.data);
    } catch (error) {
      console.error('Error fetching site config:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/programs`);
      setPrograms(response.data || []);
    } catch (error) {
      toast.error('Error al cargar el programa');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = () => {
    let checkoutUrl = `/checkout?plan=premium`;
    if (appliedDiscount?.code) {
      checkoutUrl += `&discount=${encodeURIComponent(appliedDiscount.code)}`;
    }
    navigate(checkoutUrl);
  };

  const validateDiscount = async (originalPrice) => {
    if (!discountCode.trim()) {
      toast.error('Ingresa un código de descuento');
      return;
    }

    setValidatingDiscount(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/validate-discount`, {
        code: discountCode,
        product_id: 'premium',
        original_price: originalPrice
      });

      setAppliedDiscount({
        code: response.data.code,
        discount_amount: response.data.discount_amount,
        final_price: response.data.final_price,
        message: response.data.message
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código no válido');
      setAppliedDiscount(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const clearDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const getProgramImage = () => {
    const defaultImg = 'https://images.unsplash.com/photo-1550345332-09e3ac987658?w=1200';
    if (siteConfig?.programs_images) {
      return siteConfig.programs_images.premium || siteConfig.programs_images.nivel_3 || defaultImg;
    }
    return defaultImg;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  // Buscar solo el programa Premium (Club VIP)
  const premiumProgram = programs.find(p => {
    const key = (p.level || p.program_id || p.packageId || '').toLowerCase();
    return key === 'premium' || key === 'nivel_3';
  });

  // Fallback con valores por defecto
  const program = premiumProgram || {
    name: 'Club VIP',
    price: 99,
    original_price: null,
    description: 'Acceso completo a todo el contenido de la plataforma. La experiencia definitiva para transformar tu cuerpo.',
    features: [
      'Acceso a TODOS los niveles de entrenamiento',
      '6 días de entrenamiento en video avanzado',
      'Plan de alimentación 100% personalizado, creado según tu perfil y objetivos individuales, actualizado cuando lo necesites',
      'Registro y seguimiento completo',
      'Biblioteca educativa completa',
      'Todos los Ebooks incluidos (PDF)',
      'Conversatorios exclusivos',
      'Acceso gratuito a TODOS los challenges',
      'Acceso a recetas exclusivas'
    ]
  };

  const price = program.price || 99;
  const originalPrice = program.original_price;
  const hasOriginalPrice = originalPrice && originalPrice > price;
  const features = program.features && program.features.length > 0 ? program.features : [
    'Acceso a TODOS los niveles de entrenamiento',
    '6 días de entrenamiento en video avanzado',
    'Plan de alimentación 100% personalizado, creado según tu perfil y objetivos individuales, actualizado cuando lo necesites',
    'Registro y seguimiento completo',
    'Biblioteca educativa completa',
    'Todos los Ebooks incluidos (PDF)',
    'Conversatorios exclusivos',
    'Acceso gratuito a TODOS los challenges',
    'Acceso a recetas exclusivas'
  ];

  return (
    <div className="min-h-screen bg-[#09090b] py-20 md:py-24 relative overflow-hidden">
      {/* Glow backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/5 to-transparent pointer-events-none" />
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded-full px-5 py-2 mb-6" data-testid="programs-header-badge">
            <Crown className="w-4 h-4 text-pink-400" />
            <span className="text-pink-400 text-xs font-bold uppercase tracking-[0.25em]">Membresía exclusiva</span>
          </div>
          
          <h1 className="font-barlow text-4xl md:text-6xl lg:text-7xl font-black uppercase text-white tracking-tight" data-testid="programs-title">
            CLUB <span className="text-pink-500">VIP</span>
          </h1>
        </div>

        {/* Hero Split Card */}
        <div 
          className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-[2rem] overflow-hidden border-2 border-pink-500/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-pink-950/30 shadow-[0_0_80px_rgba(236,72,153,0.15)]"
          data-testid="club-vip-card"
        >
          {/* LEFT - Image */}
          <div className="relative min-h-[420px] lg:min-h-[720px] overflow-hidden">
            <img 
              src={getProgramImage()}
              alt="Club VIP"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-zinc-900" />
          </div>

          {/* RIGHT - Content */}
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <h2 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-6 leading-[0.95]">
              PAGO <span className="text-pink-500">ANUAL</span>
            </h2>

            {/* Features */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2.5 text-zinc-200 text-sm">
                  <span className="w-5 h-5 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-pink-400" />
                  </span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Discount Input */}
            <div className="mb-6">
              <label className="text-xs text-zinc-400 flex items-center gap-2 mb-2 uppercase tracking-wider font-bold">
                <Tag className="w-3.5 h-3.5 text-pink-400" /> ¿Tienes un código de descuento?
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Ingresa tu código"
                  className="flex-1 px-4 py-3 bg-zinc-800/70 border border-zinc-700 rounded-full text-sm uppercase text-white placeholder:text-zinc-500 focus:border-pink-500 outline-none"
                  data-testid="discount-input-programs"
                />
                <button
                  onClick={() => validateDiscount(price)}
                  disabled={validatingDiscount}
                  className="px-5 py-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white rounded-full text-sm disabled:opacity-50 font-bold"
                  data-testid="apply-discount-btn-programs"
                >
                  {validatingDiscount ? '...' : 'Aplicar'}
                </button>
              </div>
              {appliedDiscount && (
                <div className="flex items-center justify-between bg-green-500/20 text-green-400 px-4 py-2.5 rounded-full text-sm border border-green-500/30 mt-3">
                  <span className="flex items-center gap-2">
                    <Check className="w-4 h-4" /> {appliedDiscount.code} aplicado
                  </span>
                  <button onClick={clearDiscount} className="text-green-400 hover:text-green-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Price + CTA */}
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-5 pt-6 border-t border-zinc-800">
              <div>
                {appliedDiscount ? (
                  <div className="flex items-baseline gap-3">
                    <span className="line-through text-2xl text-zinc-500">${price}</span>
                    <span className="font-barlow text-5xl md:text-6xl font-black text-pink-500">${appliedDiscount.final_price}</span>
                  </div>
                ) : hasOriginalPrice ? (
                  <div className="flex items-baseline gap-3">
                    <span className="line-through text-2xl text-zinc-500">${originalPrice}</span>
                    <span className="font-barlow text-5xl md:text-6xl font-black text-green-400">${price}</span>
                  </div>
                ) : (
                  <div className="flex items-baseline gap-2">
                    <span className="font-barlow text-5xl md:text-6xl font-black text-white">${price}</span>
                    <span className="text-zinc-500 text-base">/año</span>
                  </div>
                )}
                <p className="text-xs text-zinc-500 mt-1">Pago único anual · Sin letra chica</p>
              </div>
              <Button
                onClick={handlePurchase}
                className="w-full sm:w-auto bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase tracking-wider px-8 py-6 rounded-full glow-pink text-base group"
                data-testid="purchase-premium"
              >
                Quiero entrar
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>

        {/* Trust Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
            <Shield className="w-5 h-5 text-pink-500 flex-shrink-0" />
            <span className="text-sm text-zinc-300">Pago 100% seguro</span>
          </div>
          <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
            <Zap className="w-5 h-5 text-pink-500 flex-shrink-0" />
            <span className="text-sm text-zinc-300">Acceso inmediato</span>
          </div>
          <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
            <Crown className="w-5 h-5 text-pink-500 flex-shrink-0" />
            <span className="text-sm text-zinc-300">Comunidad exclusiva</span>
          </div>
        </div>

        {/* FAQ / Secondary info */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Star className="w-6 h-6 text-pink-500 mb-3" />
            <h3 className="font-barlow text-lg font-bold text-white mb-2 uppercase">¿Para quién es?</h3>
            <p className="text-sm text-zinc-400">Para la mujer que quiere transformar su cuerpo de forma inteligente, sin importar si llevas años entrenando o si simplemente no sabes por dónde empezar.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Sparkles className="w-6 h-6 text-pink-500 mb-3" />
            <h3 className="font-barlow text-lg font-bold text-white mb-2 uppercase">¿Qué incluye?</h3>
            <p className="text-sm text-zinc-400">Nutrición personalizada, entrenamiento, ebooks, challenges, podcast, recetas, comunidad, acompañamiento y mucho más — solo por $99 USD al año.</p>
          </div>
          <div className="bg-zinc-900/40 border border-zinc-800 rounded-2xl p-6">
            <Crown className="w-6 h-6 text-pink-500 mb-3" />
            <h3 className="font-barlow text-lg font-bold text-white mb-2 uppercase">¿Cómo accedo?</h3>
            <p className="text-sm text-zinc-400">Al finalizar el pago recibes acceso inmediato por email. Sin contratos, sin letra chica.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Programs;
